"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { ChevronLeft } from "lucide-react"
import { cn } from "@/lib/utils"
import { isRailWidth, SIDEBAR_MAX_WIDTH, SIDEBAR_SNAP_WIDTH } from "@/lib/layout-preferences"
import { useLayoutStore } from "@/stores/layout-store"
import { useComponentsTranslations } from "@/hooks/use-translations"

/**
 * The seam between the sidebar and the page — grab it and pull.
 *
 * This replaced a Navigation setting with three named modes. Anything the user
 * can see while they change it should be changed by hand rather than in
 * Settings, and a width the user chose themselves is right for their screen in
 * a way three presets never are.
 *
 * Below the snap point it parks as an icon rail, so the rail is a width rather
 * than a mode: nothing else in the app has to know it exists.
 */
export function SidebarResizer() {
  const t = useComponentsTranslations()
  const previewSidebarWidth = useLayoutStore((state) => state.previewSidebarWidth)
  const commitSidebarWidth = useLayoutStore((state) => state.commitSidebarWidth)
  const sidebarWidth = useLayoutStore((state) => state.sidebarWidth)
  const toggleSidebar = useLayoutStore((state) => state.toggleSidebar)

  // In the rail the sidebar carries its own expand button; two controls for
  // one action a centimetre apart is worse than either alone.
  const isRail = isRailWidth(sidebarWidth)

  const [isDragging, setIsDragging] = useState(false)
  // Read during pointermove without re-subscribing the listeners each frame.
  const latestWidth = useRef(sidebarWidth)

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragging(true)
    },
    [],
  )

  useEffect(() => {
    if (!isDragging) return

    const handleMove = (event: PointerEvent) => {
      // Measured from the viewport edge, not from a delta: the sidebar starts
      // at x=0, so the pointer's x *is* the width, and the drag cannot drift
      // out of sync with the element over a long gesture.
      const width = Math.min(SIDEBAR_MAX_WIDTH, Math.max(0, event.clientX))
      latestWidth.current = width
      previewSidebarWidth(width)
    }

    const handleUp = () => {
      setIsDragging(false)
      commitSidebarWidth(latestWidth.current)
    }

    window.addEventListener("pointermove", handleMove)
    window.addEventListener("pointerup", handleUp)

    // Without these the drag selects half the page and flickers the I-beam.
    const previousUserSelect = document.body.style.userSelect
    const previousCursor = document.body.style.cursor
    document.body.style.userSelect = "none"
    document.body.style.cursor = "col-resize"

    return () => {
      window.removeEventListener("pointermove", handleMove)
      window.removeEventListener("pointerup", handleUp)
      document.body.style.userSelect = previousUserSelect
      document.body.style.cursor = previousCursor
    }
  }, [isDragging, previewSidebarWidth, commitSidebarWidth])

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("sidebar_resize")}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      // Keyboard users get the same range in steps, since a pointer drag is
      // not something they can perform at all.
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          commitSidebarWidth(sidebarWidth - 24 < SIDEBAR_SNAP_WIDTH ? 0 : sidebarWidth - 24)
        }
        if (event.key === "ArrowRight") {
          event.preventDefault()
          commitSidebarWidth(Math.max(SIDEBAR_SNAP_WIDTH, sidebarWidth) + 24)
        }
      }}
      className={cn(
        "group absolute inset-y-0 right-0 z-20 w-1.5 translate-x-1/2 cursor-col-resize",
        "focus-visible:outline-none",
      )}
    >
      {/* The hit area is wider than the line, which stays invisible until it
          is wanted — the seam should be findable, not decorative. */}
      <div
        className={cn(
          "mx-auto h-full w-px transition-colors",
          isDragging ? "bg-primary" : "bg-transparent group-hover:bg-primary/60 group-focus-visible:bg-primary",
        )}
      />

      {/* Collapse, on the seam itself.

          It rides the border rather than sitting inside the sidebar, which is
          the only place in this layout that is neither a reserved row of dead
          space nor on top of a nav item — both of which were tried. Revealed
          by hovering anywhere in the sidebar, not just the 6px seam, or nobody
          would find it.

          `stopPropagation` on pointerdown matters: without it, clicking the
          button also starts a drag, and the release would commit whatever
          width the pointer happened to be over. */}
      {!isRail && (
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={toggleSidebar}
          aria-label={t("sidebar_hide")}
          title={`${t("sidebar_hide")} · ⌘\\`}
          className={cn(
            "absolute left-1/2 top-6 hidden -translate-x-1/2 rounded-full border bg-background p-1",
            "text-muted-foreground shadow-sm transition-opacity hover:text-foreground md:block",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            isDragging
              ? "opacity-0"
              : "opacity-0 focus-visible:opacity-100 group-hover/sidebar:opacity-100",
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
